export const CONNECTOR_OAUTH_PROTOCOL_VERSION = 1 as const;
export const CONNECTOR_OAUTH_WAKE_TYPE = "cns:connector-oauth:wake" as const;
export const CONNECTOR_OAUTH_ACK_TYPE = "cns:connector-oauth:ack" as const;
export const CONNECTOR_OAUTH_BROADCAST_CHANNEL = "cns:connector-oauth:v1";

export interface ConnectorOAuthWakeMessage {
  version: typeof CONNECTOR_OAUTH_PROTOCOL_VERSION;
  type: typeof CONNECTOR_OAUTH_WAKE_TYPE;
  attemptPublicId: string;
  callbackId: string;
}

export interface ConnectorOAuthAckMessage {
  version: typeof CONNECTOR_OAUTH_PROTOCOL_VERSION;
  type: typeof CONNECTOR_OAUTH_ACK_TYPE;
  attemptPublicId: string;
  callbackId: string;
}

export type ConnectorOAuthProtocolMessage =
  | ConnectorOAuthWakeMessage
  | ConnectorOAuthAckMessage;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object";
}

/** Validates the versioned same-origin OAuth wake/ack protocol payload. */
export function isConnectorOAuthProtocolMessage(
  value: unknown,
): value is ConnectorOAuthProtocolMessage {
  if (!isRecord(value)) return false;
  if (value.version !== CONNECTOR_OAUTH_PROTOCOL_VERSION) return false;
  if (value.type !== CONNECTOR_OAUTH_WAKE_TYPE && value.type !== CONNECTOR_OAUTH_ACK_TYPE) {
    return false;
  }
  return (
    typeof value.attemptPublicId === "string" &&
    value.attemptPublicId.trim() !== "" &&
    typeof value.callbackId === "string" &&
    value.callbackId.trim() !== ""
  );
}

/** Creates an ACK that exactly correlates to a validated callback wake. */
export function createConnectorOAuthAck(
  wake: ConnectorOAuthWakeMessage,
): ConnectorOAuthAckMessage {
  return {
    version: CONNECTOR_OAUTH_PROTOCOL_VERSION,
    type: CONNECTOR_OAUTH_ACK_TYPE,
    attemptPublicId: wake.attemptPublicId,
    callbackId: wake.callbackId,
  };
}

/** Accepts only same-origin dashboard paths for callback fallback navigation. */
export function isValidConnectorOAuthReturnPath(
  returnPath: string | null | undefined,
  origin?: string,
): returnPath is string {
  if (!returnPath) return false;
  const expectedOrigin =
    origin ?? (typeof window !== "undefined" ? window.location.origin : undefined);
  if (!expectedOrigin) return false;
  try {
    const url = new URL(returnPath, expectedOrigin);
    return (
      url.origin === expectedOrigin &&
      (url.pathname === "/dashboard" || url.pathname.startsWith("/dashboard/")) &&
      !url.pathname.includes("//")
    );
  } catch {
    return false;
  }
}

function createCallbackId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Publishes a callback wake after installing ACK listeners.
 *
 * Direct ACKs must come from the opener and same origin. BroadcastChannel supplies the COOP
 * fallback when the provider redirect detached the opener.
 *
 * @returns True when a correlated ACK arrives before the wait expires
 */
export function publishConnectorOAuthWakeAndWaitForAck(
  attemptPublicId: string,
  waitMs = 650,
): Promise<boolean> {
  const callbackId = createCallbackId();
  const wake: ConnectorOAuthWakeMessage = {
    version: CONNECTOR_OAUTH_PROTOCOL_VERSION,
    type: CONNECTOR_OAUTH_WAKE_TYPE,
    attemptPublicId,
    callbackId,
  };

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let channel: BroadcastChannel | null = null;

    const cleanup = () => {
      window.removeEventListener("message", onWindowMessage);
      channel?.removeEventListener("message", onBroadcastMessage);
      channel?.close();
      window.clearTimeout(timeout);
    };

    const settle = (acknowledged: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(acknowledged);
    };

    const matchesAck = (value: unknown): value is ConnectorOAuthAckMessage =>
      isConnectorOAuthProtocolMessage(value) &&
      value.type === CONNECTOR_OAUTH_ACK_TYPE &&
      value.attemptPublicId === attemptPublicId &&
      value.callbackId === callbackId;

    const onWindowMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return;
      if (event.source !== window.opener) return;
      if (matchesAck(event.data)) settle(true);
    };

    const onBroadcastMessage = (event: MessageEvent<unknown>) => {
      if (matchesAck(event.data)) settle(true);
    };

    window.addEventListener("message", onWindowMessage);
    try {
      channel = new BroadcastChannel(CONNECTOR_OAUTH_BROADCAST_CHANNEL);
      channel.addEventListener("message", onBroadcastMessage);
    } catch {
      channel = null;
    }

    const timeout = window.setTimeout(() => settle(false), waitMs);

    try {
      window.opener?.postMessage(wake, window.location.origin);
    } catch {
      // COOP can detach the opener. The broadcast below is the fallback.
    }
    try {
      channel?.postMessage(wake);
    } catch {
      // The timeout returns control to the callback fallback page.
    }
  });
}
